import { AbilityId, PlayerTag } from '@/components'
import type { SystemFn } from '@/core/ecs/Scheduler'
import type { AudioEngine } from '@/audio/AudioEngine'
import {
  sfxBarrier,
  sfxBoltSkip,
  sfxBraceBlock,
  sfxBrace,
  sfxBurnerCharge,
  sfxBurnerFire,
  sfxCharge,
  sfxCollapse,
  sfxCountdown,
  sfxCrate,
  sfxDash,
  sfxDenied,
  sfxDraw,
  sfxExplosion,
  sfxFall,
  sfxFizzle,
  sfxGust,
  sfxHit,
  sfxLaunch,
  sfxLunge,
  sfxMatchWin,
  sfxRetreat,
  sfxRicochet,
  sfxRoll,
  sfxSkip,
  sfxSlam,
  sfxSwap,
  sfxPickup,
  sfxPush,
  sfxRoundWin,
  sfxToss,
  sfxVortexArm,
  sfxVortexBurst,
  sfxWave,
} from '@/audio/sfx'
import { signatureFor } from '@/game/abilities/signatures'
import { ArenaResource } from '@/game/arenas/Arena'
import { Events } from '@/game/events'
import { EventsResource, MatchResource, ViewerResource } from '@/game/resources'
import type { InputManager } from '@/input/InputManager'

/**
 * Turns the same event queues the VFX system reads into sound.
 *
 * Everything is panned by world position, so a hit on the left of the screen
 * arrives in the left ear — on a shared-screen game that is genuinely useful
 * information, not just polish.
 */
export function createAudioSystem(audio: AudioEngine, input: InputManager): SystemFn {
  let lastCountdownStep = -1

  return ({ world }) => {
    if (!audio.running) return

    const events = world.resource(EventsResource)
    const arena = world.resource(ArenaResource)
    const match = world.resource(MatchResource)
    // Whoever holds the pad: the second slot when two share the screen, the
    // only one when a player has the keyboard and the pad merged.
    const padSlot = world.resource(ViewerResource).slots.at(-1)
    const extent = Math.max(arena.def.extentX, 1)
    const panOf = (x: number): number => Math.max(-1, Math.min(1, x / extent))

    for (const hit of events.read(Events.Hit)) {
      if (hit.kind === 'bump' && hit.power < 4) continue
      if (hit.braced) sfxBraceBlock(audio, panOf(hit.x))
      else sfxHit(audio, hit.power, panOf(hit.x))
      if (hit.lethal) sfxLaunch(audio, panOf(hit.x))

      // Rumble only for the pad-holding player, and only when they take the hit.
      const target = world.get(hit.target, PlayerTag)
      if (target && target.player === padSlot) {
        // Same 40 m/s reference as every other channel: the pad has to be able
        // to tell a graze from the hit that ends the round.
        input.rumble(Math.min(1, hit.power / 40), 90 + hit.power * 6)
      }
    }

    for (const bounce of events.read(Events.Bounce)) {
      sfxSkip(audio, bounce.speed, bounce.index, panOf(bounce.x))
    }

    for (const bolt of events.read(Events.BoltBounce)) {
      const pan = panOf(bolt.x)
      if (bolt.kind === 'floor') sfxBoltSkip(audio, pan)
      else if (bolt.kind === 'prop') sfxRicochet(audio, pan)
      else sfxFizzle(audio, pan)
    }

    for (const ability of events.read(Events.Ability)) {
      const pan = panOf(ability.x)
      if (ability.phase === 'fail') {
        // Only a human's refused press is information. Bots press buttons on
        // cooldown constantly, and hearing every one of them would turn the
        // cue into background noise — and make it useless for the player.
        if (match.controllers[ability.player] === 'human') sfxDenied(audio, pan)
        continue
      }
      const personal = signatureFor(world, ability.entity, ability.ability) !== undefined
      if (ability.phase === 'cast') {
        if (ability.ability === AbilityId.Push) sfxPush(audio, pan)
        // The anomaly's wind-up belongs to the anomaly. It used to play for
        // every fighter's R slot, so ХОД, ПРОКАТ and ПОДМЕНА all announced
        // themselves as an anomaly arming that was never going to appear.
        if (ability.ability === AbilityId.Vortex && !personal) sfxVortexArm(audio, pan)
        continue
      }
      if (ability.phase !== 'fire') continue
      // Personal moves are voiced from the `Signature` queue below, by move.
      if (personal) continue
      switch (ability.ability) {
        case AbilityId.Dash:
          sfxDash(audio, pan)
          break
        case AbilityId.Wave:
          sfxWave(audio, pan)
          break
        case AbilityId.Brace:
          sfxBrace(audio, pan)
          break
        default:
          break
      }
    }

    // One cue per personal move. The ear learns these faster than the eye
    // learns the animation, so a shared cue actively teaches the wrong move.
    for (const sig of events.read(Events.Signature)) {
      const pan = panOf(sig.x)
      switch (sig.move) {
        case 'barrier':
          sfxBarrier(audio, pan)
          break
        case 'charge':
          sfxCharge(audio, pan)
          break
        case 'toss':
          sfxToss(audio, pan)
          break
        case 'lunge':
          sfxLunge(audio, pan)
          break
        case 'gust':
          sfxGust(audio, pan)
          break
        case 'retreat':
          sfxRetreat(audio, pan)
          break
        case 'crate':
          sfxCrate(audio, pan)
          break
        case 'slam':
          sfxSlam(audio, pan)
          break
        case 'swap':
          sfxSwap(audio, pan)
          break
        case 'roll':
          sfxRoll(audio, pan)
          break
        case 'wave':
          sfxWave(audio, pan)
          break
        case 'anomaly':
          sfxVortexArm(audio, pan)
          break
      }
    }

    for (const explosion of events.read(Events.Explosion)) {
      if (explosion.kind === 'vortex') sfxVortexBurst(audio, panOf(explosion.x))
      else sfxExplosion(audio, panOf(explosion.x))
    }

    for (const hazard of events.read(Events.Hazard)) {
      if (hazard.kind === 'burnerCharge') sfxBurnerCharge(audio, panOf(hazard.x))
      else sfxBurnerFire(audio, panOf(hazard.x))
    }

    for (const pickup of events.read(Events.Pickup)) {
      sfxPickup(audio, pickup.kind, panOf(pickup.x))
      if (pickup.player === padSlot) input.rumble(0.4, 140)
    }

    for (const fall of events.read(Events.Fall)) {
      if (fall.player === null) continue
      sfxFall(audio, panOf(fall.x))
      if (fall.player === padSlot) input.rumble(0.9, 420)
    }

    for (const collapse of events.read(Events.ArenaCollapse)) {
      if (collapse.starting) sfxCollapse(audio)
    }

    for (const round of events.read(Events.Round)) {
      if (round.phase === 'over') {
        if (round.winner === null) sfxDraw(audio)
        else sfxRoundWin(audio)
      }
      if (round.phase === 'matchOver') sfxMatchWin(audio)
      if (round.phase === 'countdown') lastCountdownStep = -1
    }

    // Countdown beeps are derived from the timer rather than emitted as events,
    // so they stay in step even if a frame is dropped.
    if (match.phase === 'countdown') {
      const step = 3 - Math.ceil(match.phaseTimer - 0.2)
      if (step !== lastCountdownStep && step >= 0 && step <= 3) {
        lastCountdownStep = step
        sfxCountdown(audio, step)
      }
    }
  }
}
